import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, TextInput, Button, Title, Text } from '@tremor/react';
import { useAuth } from '../hooks/useAuth';
import { LoginResult } from '../services/auth';

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pending, setPending] = useState<Extract<LoginResult, { type: 'newPasswordRequired' }> | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await login(email, password);
      if (result.type === 'newPasswordRequired') {
        setPending(result);
      } else {
        navigate('/');
      }
    } catch (err: any) {
      setError(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSetNewPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (!pending) return;
    setLoading(true);
    try {
      await pending.complete(newPassword);
      navigate('/');
    } catch (err: any) {
      setError(err.message || 'Failed to set new password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50">
      <Card className="w-full max-w-md">
        <Title>Agents</Title>
        {pending ? (
          <>
            <Text className="mt-1 mb-6">Set a new password to finish signing in.</Text>
            <form onSubmit={handleSetNewPassword} className="space-y-4">
              <TextInput
                placeholder="New password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
              />
              <TextInput
                placeholder="Confirm new password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
              {error && <Text className="text-red-500">{error}</Text>}
              <Button type="submit" loading={loading} className="w-full">
                Set password & sign in
              </Button>
            </form>
          </>
        ) : (
          <>
            <Text className="mt-1 mb-6">Sign in to your account</Text>
            <form onSubmit={handleSubmit} className="space-y-4">
              <TextInput
                placeholder="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <TextInput
                placeholder="Password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              {error && <Text className="text-red-500">{error}</Text>}
              <Button type="submit" loading={loading} className="w-full">
                Sign In
              </Button>
            </form>
          </>
        )}
      </Card>
    </div>
  );
}
